import { Link, useLocation } from 'react-router-dom';

import classNames from 'classnames';
import {
    AwardIcon,
    BarChartIcon,
    BuildingIcon,
    CalendarIcon,
    DashboardIcon,
    FolderIcon,
    ListIcon,
    QuestionCircle,
    SettingsIcon,
    UploadIcon,
    UserGroupIcon,
} from 'fluxo-ui/icons';

import { ROUTES, USER_ROLES } from '../../common/constants';
import Logo from './Logo';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

interface NavItem {
    label: string;
    path: string;
    icon: React.ComponentType<any>;
    roles?: string[];
}

interface NavSection {
    title: string;
    items: NavItem[];
}

const Sidebar: React.FC = () => {
    const location = useLocation();
    const { user } = useAuthStore();
    const { sidebarOpen } = useUIStore();

    const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

    const navSections: NavSection[] = [
        {
            title: 'Overview',
            items: [
                {
                    label: 'Dashboard',
                    path: ROUTES.DASHBOARD,
                    icon: DashboardIcon,
                },
            ],
        },
        {
            title: 'Assessment',
            items: [
                {
                    label: 'Question Bank',
                    path: '/questions',
                    icon: QuestionCircle,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR, USER_ROLES.ORG_MODERATOR],
                },
                {
                    label: 'Exams',
                    path: ROUTES.EXAMS,
                    icon: CalendarIcon,
                },
                {
                    label: 'Live Quiz',
                    path: '/live-quiz',
                    icon: ListIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
                },
                {
                    label: 'Categories',
                    path: '/categories',
                    icon: FolderIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
                },
                {
                    label: 'Questions Pending',
                    path: '/questions/pending',
                    icon: QuestionCircle,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MODERATOR],
                },
                {
                    label: 'Reported Issues',
                    path: '/questions/flags',
                    icon: ListIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MODERATOR],
                },
            ],
        },
        {
            title: 'Insights',
            items: [
                {
                    label: 'Analytics',
                    path: '/analytics',
                    icon: BarChartIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
                },
                {
                    label: 'Certificates',
                    path: '/certificates',
                    icon: AwardIcon,
                },
            ],
        },
        {
            title: 'Administration',
            items: [
                // Super admin sees "Organizations" list; org_admin sees their own "Organization" settings
                ...(isSuperAdmin
                    ? [
                          {
                              label: 'Organizations',
                              path: '/organizations',
                              icon: BuildingIcon,
                              roles: [USER_ROLES.SUPER_ADMIN],
                          },
                      ]
                    : [
                          {
                              label: 'Organization',
                              path: '/organization',
                              icon: BuildingIcon,
                              roles: [USER_ROLES.ORG_ADMIN],
                          },
                      ]),
                {
                    label: 'Users',
                    path: '/users',
                    icon: UserGroupIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN],
                },
                {
                    label: 'Resources',
                    path: '/resources',
                    icon: UploadIcon,
                    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
                },
                {
                    label: 'Settings',
                    path: ROUTES.SETTINGS,
                    icon: SettingsIcon,
                },
            ],
        },
    ];

    const filteredSections = navSections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => {
                if (!item.roles) return true;
                return user?.role && item.roles.includes(user.role);
            }),
        }))
        .filter((section) => section.items.length > 0);

    const allItemPaths = filteredSections.flatMap((section) => section.items.map((item) => item.path));
    const activePath = allItemPaths
        .filter((path) => location.pathname === path || location.pathname.startsWith(path + '/'))
        .sort((a, b) => b.length - a.length)[0];

    const userName = user?.profile?.name || user?.name || user?.email || 'User';
    const userRole = user?.role?.replace(/_/g, ' ') || '';

    return (
        <>
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => useUIStore.getState().setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={classNames(
                    'fixed lg:sticky top-0 left-0 h-screen w-64 shrink-0 flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-50 lg:z-30 transition-transform duration-300 ease-in-out',
                    {
                        'translate-x-0': sidebarOpen,
                        '-translate-x-full lg:translate-x-0': !sidebarOpen,
                    },
                )}
            >
                {/* Brand block */}
                <div className="flex items-center gap-3 h-14 px-5 border-b border-slate-200 dark:border-slate-800">
                    <Link to={ROUTES.DASHBOARD} className="flex items-center gap-3 group">
                        <Logo className="w-9 h-9 shadow-sm group-hover:shadow-md transition-shadow rounded-lg" />
                        <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Testam</span>
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4 px-3">
                    {filteredSections.map((section) => (
                        <div key={section.title} className="mb-5 last:mb-0">
                            <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                {section.title}
                            </p>
                            <ul className="space-y-0.5">
                                {section.items.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = item.path === activePath;

                                    return (
                                        <li key={item.path}>
                                            <Link
                                                to={item.path}
                                                className={classNames(
                                                    'relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150',
                                                    {
                                                        'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium':
                                                            isActive,
                                                        'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200':
                                                            !isActive,
                                                    },
                                                )}
                                                onClick={() => {
                                                    // Close sidebar on mobile after click
                                                    if (window.innerWidth < 1024) {
                                                        useUIStore.getState().setSidebarOpen(false);
                                                    }
                                                }}
                                            >
                                                {isActive && (
                                                    <span
                                                        aria-hidden
                                                        className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-teal-600 dark:bg-teal-400"
                                                    />
                                                )}
                                                <Icon
                                                    className={classNames('w-5 h-5 shrink-0', {
                                                        'text-teal-600 dark:text-teal-400': isActive,
                                                    })}
                                                />
                                                <span>{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* User card */}
                {user && (
                    <div className="border-t border-slate-200 dark:border-slate-800 p-3">
                        <Link
                            to={ROUTES.PROFILE}
                            onClick={() => {
                                if (window.innerWidth < 1024) {
                                    useUIStore.getState().setSidebarOpen(false);
                                }
                            }}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <div className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600">
                                <span className="text-white text-sm font-semibold">{String(userName).charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{userName}</p>
                                {userRole && (
                                    <span className="inline-block mt-0.5 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 capitalize">
                                        {userRole}
                                    </span>
                                )}
                            </div>
                        </Link>
                    </div>
                )}
            </aside>
        </>
    );
};

export default Sidebar;
